import React, { useEffect, useState } from 'react';
import axios from 'axios';

import { Filter, Eye, FileText, PackageSearch, Search, UserCircle2 } from "lucide-react";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";


const Logs = () => {
  const [activeTab, setActiveTab] = useState("Obat");
  const [logs, setLogs] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [filters, setFilters] = useState({
    employee_id: '',
    type: '',
    role: '',
    search: '',
    sort: '',
    per_page: 10,
  });
  const [page, setPage] = useState(1);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const token = localStorage.getItem('token');

  const activeFilterCount = [filters.employee_id, filters.role, filters.type].filter(Boolean).length;

  const fetchLogs = async () => {
    try {
      const { employee_id, type, role, search, sort, per_page } = filters;
      const params = {
        ...(employee_id && { employee_id }),
        ...(type && { type }),
        ...(role && { role }),
        ...(search && { search }),
        ...(sort && { sort }),
        per_page,
        page,
      };

      const url =
        activeTab === 'Obat'
          ? `${process.env.REACT_APP_API_BASE_URL}/v1/logs/obat`
          : `${process.env.REACT_APP_API_BASE_URL}/v1/logs`;

      const res = await axios.get(url, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });

      setLogs(res.data.data || []);
      setTotalPages(res.data.meta?.last_page || 1);
    } catch (err) {
      console.error('Gagal fetch logs:', err);
    }
  };

  const fetchEmployees = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/v1/employee`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEmployees(res.data.data || []);
    } catch (err) {
      console.error('Gagal fetch employee:', err);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [filters, activeTab, page]);

  useEffect(() => {
    fetchEmployees();
  }, []);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setFilters((prev) => ({ ...prev, search: value }));
    setPage(1);
  };

  const handleFilterApply = () => {
    setShowFilterModal(false);
    setPage(1);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const resetFilter = () => {
    setFilters((prev) => ({
      ...prev,
      employee_id: '',
      type: '',
      role: '',
    }));
    setPage(1);
    setShowFilterModal(false);
  };

  return (
    <div className="p-5 min-h-screen flex flex-col space-y-6">
      <div className="bg-white shadow-lg rounded-2xl p-5 border border-gray-100">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-blue-100 rounded-2xl shadow-inner">
            <FileText className="text-blue-600" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-gray-800">Logs</h1>
            <p className="text-gray-500 text-sm">
              Menampilkan jejak aktivitas sistem untuk kebutuhan audit dan monitoring.
            </p>
          </div>
        </div>
        <nav aria-label="Breadcrumb" className="mt-3 text-gray-500 text-sm">
          <ol className="flex items-center space-x-2">
            <li>System</li>
            <li>/</li>
            <li className="text-gray-400">Logs</li>
          </ol>
        </nav>
      </div>

      <div className="bg-white p-6 rounded-2xl shadow-xl border border-gray-100">
        <div className="flex flex-wrap items-center justify-end gap-4 border-b border-gray-200 pb-4 mb-6">
          <div className="flex items-center gap-2">
            {activeFilterCount > 0 && (
              <span className="text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 px-3 py-1.5 rounded-full">
                {activeFilterCount} filter aktif
              </span>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex space-x-3 border-b border-gray-200 pb-4 mb-6">
          <button
            className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm transition ${
              activeTab === "Other"
                ? "bg-blue-100 text-blue-700 font-semibold border border-blue-200"
                : "hover:bg-gray-100 text-gray-600 border border-transparent"
            }`}
            onClick={() => {
              setActiveTab("Other");
              setPage(1);
            }}
          >
            <FileText size={18} />
            Other
          </button>
          <button
            className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm transition ${
              activeTab === "Obat"
                ? "bg-blue-100 text-blue-700 font-semibold border border-blue-200"
                : "hover:bg-gray-100 text-gray-600 border border-transparent"
            }`}
            onClick={() => {
              setActiveTab("Obat");
              setPage(1);
            }}
          >
            <PackageSearch size={18} />
            Obat
          </button>
        </div>
    
        {/* Search & Filter */}
        <div className="mb-5 flex flex-col md:flex-row md:items-center justify-between gap-3">
          <div className="relative w-full md:max-w-md">
            <input
              type="text"
              placeholder="Cari log..."
              value={filters.search}
              onChange={handleSearchChange}
              className="h-11 pl-11 pr-4 border border-gray-300 rounded-xl w-full bg-white focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300"
            />
            <Search size={17} className="absolute left-3.5 top-3.5 text-gray-400" />
          </div>
          <button
            onClick={() => setShowFilterModal(true)}
            className="h-11 px-4 bg-white border border-gray-300 rounded-xl hover:bg-gray-100 text-gray-700 flex items-center gap-2 transition"
          >
            <Filter size={16} />
            Filter
          </button>
        </div>
    
        {/* Table */}
        <div className="overflow-auto rounded-2xl border border-gray-200 shadow-sm">
          <table className="w-full text-sm text-left">
            <thead className="bg-blue-50 text-gray-700">
              <tr>
                {activeTab === "Obat" && <th className="px-4 py-3 font-semibold">Obat</th>}
                <th className="px-4 py-3 font-semibold">Referensi</th>
                <th className="px-4 py-3 font-semibold">Pesan</th>
                <th className="px-4 py-3 font-semibold">Waktu</th>
                <th className="px-4 py-3 font-semibold">Role</th>
                <th className="px-4 py-3 font-semibold">Aktor</th>
                <th className="px-4 py-3 font-semibold">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {logs.length > 0 ? (
                logs.map((log) => (
                  <tr key={log.id} className="border-t border-gray-100 hover:bg-gray-50 transition">
                    {activeTab === "Obat" && <td className="px-4 py-3">{log.obat?.name || "-"}</td>}
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-gray-100 text-gray-700 text-xs font-medium">
                        {log.ref?.code || "-"}
                      </span>
                    </td>
                    <td className="px-4 py-3">{log.message || "-"}</td>
                    <td className="px-4 py-3">{new Date(log.log_at).toLocaleString()}</td>
                    <td className="px-4 py-3 capitalize">
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-700 text-xs font-medium border border-indigo-100">
                        {log.role || "-"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="inline-flex items-center gap-2">
                        <UserCircle2 size={16} className="text-gray-400" />
                        <span>{log.employee?.name || "-"}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <button className="inline-flex items-center justify-center w-8 h-8 rounded-md text-blue-600 hover:bg-blue-50 hover:text-blue-700 transition">
                        <Eye size={18} />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={activeTab === "Obat" ? 7 : 6}
                    className="p-6 text-center text-gray-500"
                  >
                    Tidak ada data ditemukan.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
    
        {/* Pagination */}
        <div className="flex flex-col gap-3 md:flex-row md:justify-between md:items-center mt-6">
          <p className="text-sm text-gray-500">
            Menampilkan {logs.length} dari total {totalPages * filters.per_page} log
          </p>
          <div className="flex items-center space-x-2">
            <button
              className="flex items-center px-3 py-2 text-sm bg-blue-50 border border-blue-100 hover:bg-blue-100 text-gray-700 rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => handlePageChange(page - 1)}
              disabled={page <= 1}
            >
              <FiChevronLeft /> Sebelumnya
            </button>
            <span className="text-sm text-gray-600">
              Halaman <strong>{Math.min(page, totalPages)}</strong> dari <strong>{totalPages}</strong>
            </span>
            <button
              className="flex items-center px-3 py-2 text-sm bg-blue-50 border border-blue-100 hover:bg-blue-100 text-gray-700 rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => handlePageChange(page + 1)}
              disabled={page >= totalPages}
            >
              Berikutnya <FiChevronRight />
            </button>
          </div>
        </div>

      </div>

      {/* Filter Modal */}
      {showFilterModal && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-xl p-6 shadow-lg w-full max-w-md border border-gray-200">
            <h2 className="text-lg font-bold mb-4">Filter Log</h2>

            <div className="mb-3">
              <label className="block text-sm mb-1">Employee</label>
              <select
                value={filters.employee_id}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, employee_id: e.target.value }))
                }
                className="w-full h-10 px-3 border border-gray-300 rounded-lg"
              >
                <option value="">Semua</option>
                {employees.map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.full_name || emp.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-3">
              <label className="block text-sm mb-1">Role</label>
              <select
                value={filters.role}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, role: e.target.value }))
                }
                className="w-full h-10 px-3 border border-gray-300 rounded-lg"
              >
                <option value="">Semua</option>
                <option value="manager">Manager</option>
                <option value="employee">Employee</option>
              </select>
            </div>

            <div className="mb-4">
              <label className="block text-sm mb-1">Type</label>
              <input
                type="text"
                value={filters.type}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, type: e.target.value }))
                }
                className="w-full h-10 px-3 border border-gray-300 rounded-lg"
              />
            </div>

            <div className="flex justify-end gap-2">
              <button
                onClick={resetFilter}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition"
              >
                Reset
              </button>
              <button
                onClick={() => setShowFilterModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition"
              >
                Tutup
              </button>
              <button
                onClick={handleFilterApply}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                Terapkan
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Logs;
